import { useAgentComplianceReviewByShareLinkTokenQuery } from '@client/graphql/__generated__/main-operations';
import { AgentComplianceReviewByShareLinkTokenQuery } from '@client/graphql/__generated__/types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSharedPageContext } from '../../../contexts/SharedPageContext';

type AgentComplianceReviewOverview =
  AgentComplianceReviewByShareLinkTokenQuery['agentComplianceReviewByShareLinkToken'];

interface UseAgentComplianceReviewOverviewResult {
  overview: AgentComplianceReviewOverview | undefined;
  loading: boolean;
  error: ReturnType<
    typeof useAgentComplianceReviewByShareLinkTokenQuery
  >['error'];
  token: string | undefined;
  ingestionFolderUuid: string | undefined;
}

/**
 * Loads the agent compliance review overview for the current share link.
 *
 * Encapsulates the token (carried as a `?token=` query param) and folder
 * (route param) plumbing, plus the gating that defers the query until
 * {@link SharedPage} has verified the link is valid and unexpired. The query is
 * skipped until `isShareLinkVerified` is true so that expired/invalid links are
 * handled by SharedPage's expired-link card rather than this query.
 *
 * Because the link is already verified by the time the query runs, any
 * server-side (GraphQL) error means the token isn't bound to this folder, so we
 * redirect to /404. Transient network errors are not treated as not-found.
 */
export const useAgentComplianceReviewOverview =
  (): UseAgentComplianceReviewOverviewResult => {
    const { ingestionFolderUuid } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? undefined;
    const navigate = useNavigate();
    const { isShareLinkVerified } = useSharedPageContext();

    const { data, loading, error } =
      useAgentComplianceReviewByShareLinkTokenQuery({
        variables: {
          input: {
            token: token ?? '',
            ingestionFolderUuid: ingestionFolderUuid ?? '',
          },
        },
        skip: !isShareLinkVerified || !token || !ingestionFolderUuid,
        onError: ({ graphQLErrors }) => {
          if (graphQLErrors.length > 0) {
            navigate('/404');
          }
        },
      });

    return {
      overview: data?.agentComplianceReviewByShareLinkToken,
      loading,
      error,
      token,
      ingestionFolderUuid,
    };
  };
